import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import type { AuthResponse, LoginRequest } from '@/shared/types/domain';

const DEVICE_ID_STORAGE_KEY = 'leadcapture.deviceId';

/**
 * The device id is the one piece of auth-adjacent state that IS persisted
 * (in localStorage, not the token itself) -- it's a non-secret, low-entropy
 * label used purely to let "remember device" correlate refresh tokens with
 * a stable device across sessions. Its exposure carries no security risk.
 */
export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;

  const generated = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, generated);
  return generated;
}

export const authApi = {
  login: (email: string, password: string, rememberDevice: boolean) => {
    const request: LoginRequest = {
      email,
      password,
      rememberDevice,
      deviceId: getOrCreateDeviceId(),
      deviceLabel: describeCurrentDevice()
    };

    return httpClient.post<AuthResponse>(endpoints.auth.login, request, { skipAuth: true }).then((auth) => {
      // Persist the (possibly server-generated, on first login) device id.
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, auth.deviceId);
      return auth;
    });
  },

  refresh: () => httpClient.post<AuthResponse>(endpoints.auth.refresh, undefined, { skipAuth: true }),

  logout: () => httpClient.post<void>(endpoints.auth.logout, undefined, { skipAuth: true })
};

function describeCurrentDevice(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android device';
  return 'Web browser';
}
